import { Controller } from "@hotwired/stimulus"

// Hides the viewer's own name from the composer typing line. Heartbeats stay on HTTP
// from the composer; the live list arrives on the conversation Turbo stream the frame
// already subscribes to. Broadcasts omit current_user_id, so every tab gets the same
// markup and this controller is what keeps you out of your own sentence.
export default class extends Controller {
  static targets = [ "list", "copy", "nameTemplate" ]
  static values = { userId: Number }

  connect() {
    this.observer = new MutationObserver(() => this.hideSelf())
    this.observer.observe(this.element, { childList: true, subtree: true })
    this.hideSelf()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  hideSelf() {
    if (!this.hasListTarget) return

    // Rewriting the copy is a childList mutation. Without disconnecting, this
    // observer would call hideSelf again and hang the tab.
    this.observer.disconnect()

    const userId = String(this.userIdValue)
    const names = []
    this.listTarget.querySelectorAll("[data-user-id]").forEach((person) => {
      const isSelf = person.dataset.userId === userId
      person.hidden = isSelf
      if (!isSelf && person.dataset.userName) names.push(person.dataset.userName)
    })
    this.listTarget.hidden = names.length === 0
    this.renderCopy(names)

    if (names.length) {
      this.listTarget.setAttribute("aria-label", this.sentence(names))
    } else {
      this.listTarget.removeAttribute("aria-label")
    }

    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  renderCopy(names) {
    if (!this.hasCopyTarget) return

    this.copyTarget.replaceChildren()
    if (names.length === 0) return

    names.forEach((name, index) => {
      if (index > 0) this.copyTarget.append(this.glue(index, names.length))
      this.copyTarget.append(this.nameNode(name))
    })
    this.copyTarget.append(names.length === 1 ? " is typing…" : " are typing…")
  }

  nameNode(name) {
    if (this.hasNameTemplateTarget) {
      const node = this.nameTemplateTarget.content.cloneNode(true)
      node.querySelector(".composer-typers__name").textContent = name
      return node
    }

    return name
  }

  glue(index, length) {
    if (length === 2) return " and "
    return index === length - 1 ? ", and " : ", "
  }

  sentence(names) {
    if (names.length === 1) return `${names[0]} is typing…`
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]} are typing…`
  }
}
